import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useNavigate } from 'react-router-dom';
import FluxpromptLogo from '../../assets/icons/fluxprompt-logo.svg';
import { useAuthStore } from '../../store/storeAuth';
import { ITypeNode } from '../NewFormBuilder/Context/FormBuilderContext';
import {
  PublishedFormProvider,
  usePublishedForm,
} from './Context/PublishedFormContext';
import { Image } from './Nodes/Image';
import { Input } from './Nodes/Input';
import { List } from './Nodes/List';
import { Output } from './Nodes/Output';
import { ShortText } from './Nodes/ShortText';
import { Submit } from './Nodes/Submit';
import { Title } from './Nodes/Title';

const ResponsiveReactGridLayout = WidthProvider(Responsive) as any;

function NewPublishedFormContent({ isPreview }: { isPreview: boolean }) {
  const navigate = useNavigate();
  const {
    publishedForm,
    publishedNodes,
    errorType,
    isLoadingForm,
    isSubmitting,
  } = usePublishedForm();

  const user = useAuthStore(state => state.user);

  const breakpoints = useMemo(
    () => ({
      lg: 1200,
      md: 996,
      sm: 768,
      xs: 480,
      xxs: 0,
    }),
    [],
  );

  const cols = useMemo(
    () => ({
      lg: 12,
      md: 10,
      sm: 6,
      xs: 4,
      xxs: 2,
    }),
    [],
  );

  const generateResponsiveLayouts = useMemo(() => {
    if (!publishedNodes || publishedNodes.length === 0) {
      return { lg: [], md: [], sm: [], xs: [], xxs: [] };
    }

    const layouts: { [key: string]: any[] } = {
      lg: [],
      md: [],
      sm: [],
      xs: [],
      xxs: [],
    };

    const sortedNodes = [...publishedNodes].sort((a, b) => {
      const yA = a.layout.y;
      const yB = b.layout.y;
      if (yA !== yB) {
        return yA - yB;
      }
      return a.layout.x - b.layout.x;
    });

    Object.keys(cols).forEach((breakpoint) => {
      const numCols = cols[breakpoint as keyof typeof cols];
      const lgCols = cols.lg;
  
      const isMobile = breakpoint === 'xs' || breakpoint === 'xxs';

      layouts[breakpoint] = sortedNodes.map((node, index) => {
        const lgLayout = node.layout;
        
        if (isMobile) {
          let cumulativeY = 0;
          for (let i = 0; i < index; i++) {
            const prevNode = sortedNodes[i];
            cumulativeY += prevNode.layout.h;
          }
          
          return {
            ...lgLayout,
            i: node.id,
            x: 0,
            y: cumulativeY,
            w: numCols, 
          };
        }
        
        const ratio = numCols / lgCols;
        const newX = Math.floor(lgLayout.x * ratio);
        const newW = Math.max(1, Math.floor(lgLayout.w * ratio));
        
        return {
          ...lgLayout,
          i: node.id,
          x: Math.min(newX, numCols - newW),
          w: newW,
        };
      });
    });

    return layouts;
  }, [publishedNodes, cols]);

  useEffect(() => {
    if (isSubmitting) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isSubmitting]);

  if (isLoadingForm || !publishedForm || !publishedNodes) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1001,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (errorType && !isPreview) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1001,
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: '400px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            boxShadow: '0 0 10px rgba(0,0,0,0.1)',
            gap: '30px',
            p: '20px',
            border: '1px solid #ccc',
            borderRadius: '5px',
            height: '400px',
          }}
        >
          <Box>
            <img
              style={{
                maxWidth: '350px',
                width: '100%',
                height: '100px',
              }}
              src={FluxpromptLogo}
              alt="Fluxprompt Logo"
            />
          </Box>

          {errorType === 'login' && (
            <>
              <Typography mb={'24px'} variant="h1">
                You need to be logged in to access this form
              </Typography>

              <Button
                onClick={() => {
                  navigate('/login');
                }}
                variant="contained"
                fullWidth
              >
                Login
              </Button>
            </>
          )}

          {(errorType === 'notPublished' || errorType === 'notFound') && (
            <>
              <Typography variant="h1">
                Sorry, this form is not published or its not available
              </Typography>

              <Button
                onClick={() => {
                  navigate('/login');
                }}
                variant="contained"
                fullWidth
              >
                {user?.id ? 'Go To Dashboard' : 'Login '}
              </Button>
            </>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <div
      style={{
        width: '100vw',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: publishedForm.formStyle?.backgroundColor
          ? publishedForm.formStyle.backgroundColor
          : '#F9FAFB',
        backgroundImage: publishedForm.formStyle?.backgroundImage
          ? `url(${publishedForm.formStyle.backgroundImage})`
          : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ position: 'relative', width: '1300px' }}>
        <ResponsiveReactGridLayout
          className="layout"
          layouts={generateResponsiveLayouts}
          breakpoints={breakpoints}
          cols={cols}
          rowHeight={30}
          isDraggable={false}
          isResizable={false}
          verticalCompact={false}
          margin={[10, 10]}
          containerPadding={[10, 10]}
        >
          {publishedNodes.map(node => (
            <div
              key={node.id}
              style={{
                border: `${node.data.styles.borderWeight}px solid ${node.data.styles.borderColor}`,
                borderRadius: node.data.styles.borderRadius,
                backgroundColor: node.data.styles.backgroundColor,
                boxSizing: 'border-box',
                position: 'relative',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  padding: `${node.data.styles.padding !== undefined && node.data.styles.padding !== null ? node.data.styles.padding : 16}px`,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: node.data.styles.verticalAlign,
                }}
              >
                {node.type === ITypeNode.TITLE && <Title node={node} />}

                {node.type === ITypeNode.SHORT_TEXT && (
                  <ShortText node={node} />
                )}

                {node.type === ITypeNode.IMAGE && <Image node={node} />}

                {node.type === ITypeNode.LIST && <List node={node} />}

                {node.type === ITypeNode.INPUT && <Input node={node} />}

                {node.type === ITypeNode.OUTPUT && <Output node={node} />}

                {node.type === ITypeNode.SUBMIT && <Submit node={node} />}
              </div>
            </div>
          ))}
        </ResponsiveReactGridLayout>

        {isSubmitting && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9999,
              backdropFilter: 'blur(2px)',
            }}
            onClick={e => e.preventDefault()}
            onWheel={e => e.preventDefault()}
            onTouchMove={e => e.preventDefault()}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                backgroundColor: 'white',
                padding: 4,
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="h6" sx={{ color: '#1F2937' }}>
                {publishedForm.formSettings?.loadingMessage &&
                publishedForm.formSettings?.loadingMessage !== ''
                  ? publishedForm.formSettings.loadingMessage
                  : 'Processing...'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </div>
  );
}

export default function NewPublishedForm({
  isPreview = false,
}: {
  isPreview?: boolean;
}) {
  return (
    <PublishedFormProvider isPreview={isPreview}>
      <NewPublishedFormContent isPreview={isPreview} />
    </PublishedFormProvider>
  );
}
